import { createContext, useContext } from "react";
import type { RegisterWithTenantInput } from "@webtemplate/schemas";
import type { UserSummary } from "../types/api";

/**
 * Auth contract types + context + the pure `useAuth` consumer. The PROVIDER
 * (login/refresh/tRPC) lives in the consuming app (client/src/core/auth); this
 * package owns only the context object + contract so plugins can read auth
 * without coupling to the app (D-20). Single React instance keeps identity stable.
 */

export interface AuthUser extends UserSummary {
  readonly roles?: readonly string[];
  readonly permissions?: readonly string[];
  /** Coarse role flags from the server (me/login) — drive nav + route guards. */
  readonly isAdmin?: boolean;
  readonly isSuperAdmin?: boolean;
  /** The membership tenant id from `me` (D-08) — drives the Account tenant pill. */
  readonly tenantId?: string;
}

export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
  readonly rememberMe?: boolean;
}

export interface RegisterCredentials {
  readonly email: string;
  readonly password: string;
  readonly confirmPassword: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly acceptTerms: boolean;
}

/**
 * Credentials for self-service signup (user + new tenant in one call).
 * Aliased to the shared Zod schema's inferred type so the client cannot
 * drift from the server contract.
 */
export type RegisterWithTenantCredentials = RegisterWithTenantInput;

export interface TwoFactorVerification {
  readonly code: string;
  readonly sessionToken: string;
}

export interface TwoFactorBackupVerification {
  readonly backupCode: string;
  readonly sessionToken: string;
}

export interface ForgotPasswordRequest {
  readonly email: string;
}

export interface ResetPasswordRequest {
  readonly token: string;
  readonly password: string;
  readonly confirmPassword: string;
  /** 2FA proof supplied at the reset step for accounts with 2FA enabled (CR-01). */
  readonly twoFactorMethod?: "totp" | "email_otp" | "recovery_code";
  readonly twoFactorCode?: string;
}

export interface AuthResponse {
  readonly accessToken: string;
  readonly user: AuthUser;
}

export interface TwoFactorRequiredResponse {
  readonly requiresTwoFactor: true;
  readonly sessionToken: string;
}

export type LoginResult = AuthResponse | TwoFactorRequiredResponse;

export interface SimpleResponse {
  readonly success: boolean;
  readonly message: string;
}

/**
 * Response from forgotPassword. Deliberately a plain generic acknowledgement:
 * the endpoint returns a byte-identical body for every input and never reveals
 * account existence or 2FA status (CR-01) — the 2FA challenge is handled at the
 * reset step instead.
 */
export type ForgotPasswordResponse = SimpleResponse;

/** Response from resetPassword. `requiresTwoFactor` prompts the client for a 2FA code (CR-01). */
export interface ResetPasswordResponse extends SimpleResponse {
  readonly requiresTwoFactor?: boolean;
}

export type AuthStatus =
  | "idle"
  | "loading"
  | "authenticated"
  | "unauthenticated";

export interface AuthState {
  readonly user: AuthUser | null;
  readonly status: AuthStatus;
  readonly accessToken: string | null;
}

/** Dedicated response for registration (user is NOT authenticated yet) */
export interface RegisterResponse {
  readonly success: boolean;
  readonly message: string;
}

export interface AuthContextValue {
  readonly user: AuthUser | null;
  readonly status: AuthStatus;
  readonly isAuthenticated: boolean;
  readonly isAdmin: boolean;
  readonly isSuperAdmin: boolean;
  readonly login: (credentials: LoginCredentials) => Promise<LoginResult>;
  readonly register: (
    credentials: RegisterCredentials,
  ) => Promise<RegisterResponse>;
  readonly registerWithTenant: (
    credentials: RegisterWithTenantCredentials,
  ) => Promise<RegisterResponse>;
  readonly verifyTwoFactor: (
    verification: TwoFactorVerification,
  ) => Promise<AuthResponse>;
  readonly verifyBackupCode: (
    verification: TwoFactorBackupVerification,
  ) => Promise<AuthResponse>;
  readonly verifyEmailOtp: (
    verification: TwoFactorVerification,
  ) => Promise<AuthResponse>;
  readonly requestEmailOtp: (sessionToken: string) => Promise<SimpleResponse>;
  readonly requestMagicLink: (email: string) => Promise<SimpleResponse>;
  readonly forgotPassword: (
    request: ForgotPasswordRequest,
  ) => Promise<ForgotPasswordResponse>;
  readonly resetPassword: (
    request: ResetPasswordRequest,
  ) => Promise<ResetPasswordResponse>;
  readonly logout: () => void | Promise<void>;
}

export function isTwoFactorRequired(
  result: LoginResult,
): result is TwoFactorRequiredResponse {
  return "requiresTwoFactor" in result && result.requiresTwoFactor === true;
}

/**
 * Auth context. Null until an AuthProvider (in the app) mounts above the tree.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/** Read the current auth context. Throws if used outside an AuthProvider. */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
